// src/pages/Counselling/Requests/Components/ApproveRequestModal.tsx
/**
 * Approving a care request: creates the `CareCase`, its counselee members, and optionally assigns the
 * owning leader, all in one `approve()` call.
 *
 * ⚠️ **This is irreversible.** There is no un-approve endpoint, and approval matches or creates member
 * records. The safeguards live in the wizard rather than in where it can be opened from: the member
 * matches get a step of their own, the Review step restates every choice, the close button is locked
 * while busy, and the final button names the leader so it cannot be pressed on autopilot.
 *
 * ⓘ It opens from the queue's row action as well as the detail page — unlike `PendingCases`, where
 * Accept merely claims work already assigned, this creates records, so the wizard's Review step is
 * what makes a row action acceptable.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Card, Group, Modal, Stack, Text, Textarea,
} from '@mantine/core';
import {
  IconAlertTriangle, IconCheck, IconInfoCircle, IconMapPin, IconUserCheck, IconUsers,
} from '@tabler/icons-react';
import { Select } from '@/components';
import MultiStepForm, { type FormStep } from '@/components/advanced/forms/MultiStepForm';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useCareRequestService, useCareZoneService, useLocationService,
  type CareRequestModelView, type MemberCandidateQuery, type SubjectMemberChoice,
} from '@actschurch/ccms-sdk';
import MemberMatchPicker, { type MemberChoice } from '@/components/common/MemberMatchPicker';
import LeaderPicker from './LeaderPicker';

interface NamedLookup { value: string; label: string }

export interface ApproveRequestModalProps {
  request: CareRequestModelView | null;
  opened: boolean;
  onClose: () => void;
  /** Called with the new case id once the case exists, so the caller can offer a link to it. */
  onApproved: (caseID: number | null) => void;
}

export default function ApproveRequestModal({
  request,
  opened,
  onClose,
  onApproved,
}: ApproveRequestModalProps) {
  const requestService = useCareRequestService();
  const zoneService = useCareZoneService();
  const locationService = useLocationService();

  const [leaderUserId, setLeaderUserId] = useState<string | null>(null);
  const [zoneID, setZoneID] = useState<string | null>(null);
  const [locationID, setLocationID] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * Per-subject member resolution, keyed by `subjectID`.
   *
   * ⚠️ Absent or `null` means "untouched", and untouched subjects are **left out of the payload** so
   * approval behaves exactly as it did before this picker existed.
   */
  const [memberChoices, setMemberChoices] = useState<Record<number, MemberChoice>>({});

  const [zones, setZones] = useState<NamedLookup[]>([]);
  const [locations, setLocations] = useState<NamedLookup[]>([]);

  // Reset per request, and prefill placement from what the requester already told us.
  useEffect(() => {
    if (!opened || !request) return;
    setLeaderUserId(null);
    setNote('');
    setMemberChoices({});
    setZoneID(request.zoneID != null ? String(request.zoneID) : null);
    setLocationID(request.locationID != null ? String(request.locationID) : null);
  }, [opened, request]);

  useEffect(() => {
    if (!opened) return;
    const toOptions = (list: unknown, idKey: string): NamedLookup[] =>
      (Array.isArray(list) ? list : []).map((r) => {
        const row = r as Record<string, unknown>;
        return { value: String(row[idKey]), label: String(row.name ?? '') };
      });

    Promise.allSettled([zoneService.getAll(), locationService.getAll()]).then(([z, l]) => {
      if (z.status === 'fulfilled') setZones(toOptions(z.value, 'zoneID'));
      if (l.status === 'fulfilled') setLocations(toOptions(l.value, 'locationID'));
    });
  }, [opened, zoneService, locationService]);

  const fetchCandidates = useCallback(
    (query: MemberCandidateQuery) => requestService.candidateMatches(query),
    [requestService],
  );

  const subjects = useMemo(() => request?.subjects ?? [], [request]);

  /** Only the subjects the admin actually resolved. Empty array → omit the field entirely. */
  const subjectMembers = useMemo<SubjectMemberChoice[]>(
    () => subjects.flatMap((sub) => {
      const choice = memberChoices[sub.subjectID];
      if (!choice) return [];
      return [{ subjectID: sub.subjectID, ...choice }];
    }),
    [subjects, memberChoices],
  );

  const handleApprove = async () => {
    if (!request || busy) return;
    setBusy(true);
    try {
      const res = await requestService.approve(request.requestID, {
        leaderUserId: leaderUserId ?? null,
        zoneID: zoneID ? Number(zoneID) : null,
        locationID: locationID ? Number(locationID) : null,
        note: note.trim() || null,
        // ⚠️ `null`, not `[]`, when nothing was picked — an empty array could read as "resolve nobody".
        subjectMembers: subjectMembers.length > 0 ? subjectMembers : null,
      });

      // ⚠️ Read through the envelope. The method is typed `Promise<CareCase>`, but this API returns
      // `ResponseData`-wrapped payloads in several places, and navigating to `/Cases/undefined` on a
      // miss is worse than not offering the link.
      const created = ((res as unknown as { data?: { caseID?: number } })?.data ?? res) as { caseID?: number };
      const caseID = typeof created?.caseID === 'number' ? created.caseID : null;

      onApproved(caseID);
      onClose();
    } catch (e) {
      console.error('[ApproveRequest] approve failed:', e);
      toast.error('Could not approve', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const requesterGender = request?.gender?.trim();

  /**
   * The approval split into steps.
   *
   * ⚠️ Mirrors the intake wizard deliberately: the two queues are worked by the same people, and one
   * long modal made the irreversible bits — the member matches especially — easy to skim past. Each
   * step asks one question and the summary restates every choice before the final button.
   */
  const steps: FormStep[] = request ? [
    {
      title: 'People',
      description: subjects.length === 1 ? '1 counselee' : `${subjects.length} counselees`,
      icon: <IconUsers size={18} />,
      content: (
        <Stack gap="md">
          <div>
            <Text size="sm" fw={600}>
              {subjects.length === 1 ? 'Who needs care' : `${subjects.length} people need care`}
            </Text>
            <Text size="xs" c="dimmed">
              Each becomes a counselee on the case. Leave a person on{' '}
              <Text span fw={600}>Match automatically</Text> unless you can see they already exist
              under a different email or phone.
            </Text>
          </div>

          {subjects.length === 0 ? (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">
                No separate counselees were staged on this request, so the person named on it becomes
                the counselee.
              </Text>
            </Alert>
          ) : (
            <Stack gap="xs">
              {subjects.map((sub) => (
                <MemberMatchPicker
                  key={sub.subjectID}
                  personName={[
                    sub.fullName
                      || [sub.firstName, sub.surname].filter(Boolean).join(' ').trim()
                      || 'Unnamed',
                    sub.role ? `(${sub.role})` : sub.isMain ? '(main)' : '',
                  ].filter(Boolean).join(' ')}
                  query={{
                    firstName: sub.firstName,
                    lastName: sub.surname,
                    email: sub.email,
                    phone: sub.phone,
                  }}
                  fetchCandidates={fetchCandidates}
                  value={memberChoices[sub.subjectID] ?? null}
                  onChange={(choice) => setMemberChoices((prev) => ({
                    ...prev,
                    [sub.subjectID]: choice,
                  }))}
                />
              ))}
            </Stack>
          )}
        </Stack>
      ),
    },
    {
      title: 'Leader',
      description: leaderUserId ? 'Selected' : 'Optional',
      icon: <IconUserCheck size={18} />,
      content: (
        <Stack gap="sm">
          {/* ⚠️ Advisory only. `LeaderModelView.gender` is best-effort — it comes from the member
              cache and is null when that record has none — so matches are sorted and badged, never
              filtered. */}
          {request.preferSameGenderLeader && (
            <Alert color="yellow" variant="light" icon={<IconUserCheck size={16} />} title="Same-gender leader preferred">
              <Text size="sm">
                {requesterGender
                  ? `The requester asked for a same-gender leader. They gave their gender as ${requesterGender}.`
                  : 'The requester asked for a same-gender leader but did not give their gender.'}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                {requesterGender
                  ? 'Matching leaders are listed first and badged in green. Leaders whose member record has no gender on file show no badge.'
                  : 'Without their gender there is nothing to match against, so the list is not reordered.'}
              </Text>
            </Alert>
          )}

          <div>
            <Text size="sm" fw={600}>Assign a leader</Text>
            <Text size="xs" c="dimmed">
              Optional. Starts with leaders who already cover this need and zone.
            </Text>
          </div>
          <LeaderPicker
            needID={request.needID}
            zoneID={request.zoneID}
            preferGender={request.preferSameGenderLeader ? request.gender : null}
            value={leaderUserId}
            onChange={setLeaderUserId}
          />
        </Stack>
      ),
    },
    {
      title: 'Placement',
      description: 'Zone and location',
      icon: <IconMapPin size={18} />,
      content: (
        <Stack gap="sm">
          <Text size="sm" fw={600}>Where does this case sit?</Text>
          <Group grow align="flex-start">
            <Select
              label="Zone"
              placeholder="No zone"
              data={zones}
              value={zoneID}
              onChange={setZoneID}
              searchable
              clearable
            />
            <Select
              label="Location"
              placeholder="No location"
              data={locations}
              value={locationID}
              onChange={setLocationID}
              searchable
              clearable
            />
          </Group>
        </Stack>
      ),
    },
    {
      title: 'Review',
      description: 'Confirm and create',
      icon: <IconCheck size={18} />,
      content: (
        <Stack gap="md">
          {/* ⚠️ Restates every choice. Approval creates the case and its member records and cannot be
              undone, so the last screen should not require remembering three steps back. */}
          <Card withBorder radius="md" padding="md">
            <Stack gap={6}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Need</Text>
                <Text size="sm" fw={600}>{request.needName ?? '—'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Counselees</Text>
                <Text size="sm" fw={600}>
                  {subjects.length > 0
                    ? `${subjects.length} ${subjects.length === 1 ? 'person' : 'people'}`
                    : 'the person named on the request'}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Leader</Text>
                <Text size="sm" fw={600}>{leaderUserId ? 'Assigned' : 'Unassigned'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Placement</Text>
                <Text size="sm" fw={600}>
                  {[zones.find((z) => z.value === zoneID)?.label,
                    locations.find((l) => l.value === locationID)?.label]
                    .filter(Boolean).join(' · ') || 'Not set'}
                </Text>
              </Group>
            </Stack>
          </Card>

          <Textarea
            label="Note"
            description="Anything the leader should know before making contact. Optional."
            autosize
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />

          {!leaderUserId && (
            <Alert color="gray" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                No leader selected. The case will be created unassigned, ready for a leader to be
                assigned later.
              </Text>
            </Alert>
          )}
        </Stack>
      ),
    },
  ] : [];

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title="Approve request and create case"
      size="xl"
      centered
    >
      {request && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {request.reference ?? `Request #${request.requestID}`} from{' '}
            <Text span fw={600}>{request.fullName ?? 'Unknown'}</Text> for{' '}
            <Text span fw={600}>{request.needName ?? 'a need'}</Text>.
          </Text>

          <MultiStepForm
            steps={steps}
            onComplete={handleApprove}
            onCancel={onClose}
            loading={busy}
            completeLabel={leaderUserId ? 'Approve and assign' : 'Approve without a leader'}
          />
        </Stack>
      )}
    </Modal>
  );
}
